export const MAX_APPROVAL_REVIEWERS = 10;

export const normalizeApprovalReviewersMap = (reviewersMap = {}) =>
  Object.entries(reviewersMap || {})
    .map(([reviewerID, reviewer]) => ({
      reviewerID,
      email: reviewer?.email || "",
      url: reviewer?.url || "",
      status: reviewer?.status || "pending",
    }))
    .sort((a, b) => a.reviewerID.localeCompare(b.reviewerID));

export const getFirstReviewerUrl = ({ reviewers, fallbackUrl = null }) => {
  const reviewersList = normalizeApprovalReviewersMap(reviewers);
  return reviewersList[0]?.url || fallbackUrl || null;
};

export const hasMultipleApprovalReviewers = (reviewers) => {
  if (Array.isArray(reviewers)) {
    return reviewers.length > 1;
  }

  return normalizeApprovalReviewersMap(reviewers).length > 1;
};

export const deriveApprovalStatusFromReviewers = ({
  reviewers = [],
  fallbackStatus = null,
}) => {
  const reviewersList = Array.isArray(reviewers)
    ? reviewers
    : normalizeApprovalReviewersMap(reviewers);

  if (!hasMultipleApprovalReviewers(reviewersList)) {
    return fallbackStatus;
  }

  const statuses = reviewersList.map(
    (reviewer) => reviewer?.status || "pending"
  );

  if (statuses.every((status) => status === "approved")) {
    return "approved";
  }

  if (statuses.some((status) => status === "rejected")) {
    return "rejected";
  }

  return "pending";
};

export const normalizeReviewerApprovalsMap = (reviewerApprovalsMap = {}) =>
  Object.entries(reviewerApprovalsMap || {})
    .map(([reviewerID, approval]) => ({
      reviewerID,
      status: approval?.status || "pending",
    }))
    .sort((a, b) => a.reviewerID.localeCompare(b.reviewerID));

const normalizeReviewerApprovalsRecord = (reviewerApprovalsMap = {}) =>
  normalizeReviewerApprovalsMap(reviewerApprovalsMap).reduce(
    (normalizedApprovals, { reviewerID, status }) => ({
      ...normalizedApprovals,
      [reviewerID]: { status },
    }),
    {}
  );

export const mergeReviewerApprovalsIntoContentMap = ({
  currentApprovals = {},
  reviewerApprovals = {},
}) => ({
  ...currentApprovals,
  ...normalizeReviewerApprovalsRecord(reviewerApprovals),
});

export const mergeReviewersWithApprovals = ({
  reviewers = [],
  reviewerApprovals = {},
}) => {
  const reviewersList = Array.isArray(reviewers)
    ? reviewers
    : normalizeApprovalReviewersMap(reviewers);
  const approvalsByReviewerID =
    normalizeReviewerApprovalsRecord(reviewerApprovals);

  return reviewersList.map((reviewer) => ({
    ...reviewer,
    status:
      approvalsByReviewerID[reviewer.reviewerID]?.status ||
      reviewer?.status ||
      "pending",
  }));
};

export const getApprovalReviewersList = (approval) => {
  if (!approval?.reviewers) {
    return [];
  }

  return Array.isArray(approval.reviewers)
    ? approval.reviewers
    : normalizeApprovalReviewersMap(approval.reviewers);
};

export const getMyReviewerStatusForCampaign = ({
  approval = null,
  currentReviewerID = "",
} = {}) => {
  if (!approval) {
    return null;
  }

  const globalStatus = approval.status ?? "pending";
  const reviewers = getApprovalReviewersList(approval);

  if (!hasMultipleApprovalReviewers(reviewers)) {
    return globalStatus;
  }

  const normalizedReviewerID = String(currentReviewerID || "").trim();
  if (!normalizedReviewerID) {
    return globalStatus;
  }

  const reviewer = reviewers.find(
    (item) => item.reviewerID === normalizedReviewerID
  );

  return reviewer?.status || "pending";
};

export const getCampaignReviewerPermissions = ({
  approval = null,
  currentReviewer = null,
} = {}) => {
  const globalStatus = approval?.status ?? null;
  const currentReviewerID = currentReviewer?.reviewerID || "";
  const reviewers = getApprovalReviewersList(approval);
  const isMultiReviewer = hasMultipleApprovalReviewers(reviewers);
  const myReviewerStatus = getMyReviewerStatusForCampaign({
    approval,
    currentReviewerID,
  });

  const commentsLocked = !approval || myReviewerStatus === "approved";

  let canApprove = false;
  if (approval && globalStatus !== "approved") {
    if (isMultiReviewer) {
      canApprove = myReviewerStatus === "pending";
    } else {
      canApprove = globalStatus === "pending";
    }
  }

  return {
    isMultiReviewer,
    commentsLocked,
    canApprove,
    myReviewerStatus,
  };
};

export const mergeReviewerStatusIntoApproval = ({
  approval = null,
  reviewerID = "",
  status = "pending",
} = {}) => {
  if (!approval || !reviewerID) {
    return approval;
  }

  const normalizedReviewerID = String(reviewerID).trim();
  const reviewers = getApprovalReviewersList(approval);

  if (!hasMultipleApprovalReviewers(reviewers)) {
    return approval;
  }

  const hasReviewer = reviewers.some(
    (item) => item.reviewerID === normalizedReviewerID
  );

  const nextReviewers = hasReviewer
    ? reviewers.map((item) =>
        item.reviewerID === normalizedReviewerID ? { ...item, status } : item
      )
    : [
        ...reviewers,
        {
          reviewerID: normalizedReviewerID,
          email: "",
          url: "",
          status,
        },
      ];

  return {
    ...approval,
    reviewers: nextReviewers,
  };
};
